import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, FolderOpen, LayoutGrid, List, Plus, Settings, Sparkles } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCreateOrganization, useOrganizations } from '@/api/organizations';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { cn } from '@/lib/cn';

const STATUS_TONE: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
  active: 'green',
  pending: 'yellow',
  suspended: 'red',
  archived: 'gray',
};

type ViewMode = 'grid' | 'list';

export function OrganizationsPage() {
  usePageTitle('Organizations');
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useOrganizations();
  const create = useCreateOrganization();
  const toast = useToast();
  const { hasRole } = useAuth();
  const canManage = hasRole(['sysadmin', 'manager']);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [view, setView] = useState<ViewMode>('grid');

  const handleCreate = async () => {
    try {
      const org = await create.mutateAsync({ name: name.trim(), slug: slug.trim() });
      toast.success('Organization created');
      setOpen(false);
      setName('');
      setSlug('');
      navigate(`/workspace/${org.id}`);
    } catch (e) {
      toast.error('Create failed', apiError(e));
    }
  };

  const active = (data ?? []).filter((o) => o.status === 'active' || o.status === 'pending');
  const rest = (data ?? []).filter((o) => o.status !== 'active' && o.status !== 'pending');

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-bold text-[#12122C]">Organizations</h1>
          <p className="text-xs text-[#676687] mt-0.5">Click an organization to browse its files.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex border border-[#E3E3F1] rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setView('grid')}
              title="Grid view"
              className={cn('p-2 transition-colors', view === 'grid' ? 'bg-[#EFF0FF] text-[#0410BD]' : 'bg-white text-[#676687] hover:bg-[#F2F2F8]')}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              title="List view"
              className={cn('p-2 transition-colors border-l border-[#E3E3F1]', view === 'list' ? 'bg-[#EFF0FF] text-[#0410BD]' : 'bg-white text-[#676687] hover:bg-[#F2F2F8]')}
            >
              <List size={15} />
            </button>
          </div>
          {canManage && (
            <>
              <Button variant="primary" onClick={() => navigate('/admin/onboard')} className="text-xs w-auto">
                <Sparkles size={14} /> <span className="hidden sm:inline">Onboard client</span>
              </Button>
              <Button variant="secondary" onClick={() => setOpen(true)} className="text-xs w-auto">
                <Plus size={14} /> <span className="hidden sm:inline">New</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : isError ? (
        <div className="text-sm text-red-600">{apiError(error)}</div>
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState
          title="No organizations yet"
          description="Onboard your first client to set up their workspace and invite users."
          icon={<Sparkles size={24} />}
          action={canManage ? (
            <Button variant="primary" onClick={() => navigate('/admin/onboard')} className="w-auto">
              <Sparkles size={14} /> Onboard new client
            </Button>
          ) : undefined}
        />
      ) : view === 'grid' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {active.map((org) => (
              <OrgCard key={org.id} org={org} />
            ))}
          </div>
          {rest.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#BABACE] mb-2">Archived / Suspended</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {rest.map((org) => (
                  <OrgCard key={org.id} org={org} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* List view */
        <div className="bg-white border border-[#E3E3F1] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E3E3F1] text-xs font-semibold uppercase tracking-wide text-[#676687]">
                <th className="py-2.5 px-4 text-left">Name</th>
                <th className="py-2.5 px-4 text-left hidden sm:table-cell">Slug</th>
                <th className="py-2.5 px-4 text-left">Status</th>
                <th className="py-2.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...active, ...rest].map((org) => (
                <tr key={org.id} className="border-b border-[#F2F2F8] last:border-0 hover:bg-[#F9F9FC] group">
                  <td className="py-3 px-4">
                    <Link to={`/workspace/${org.id}`} className="flex items-center gap-2.5 min-w-0">
                      <span className="w-7 h-7 rounded-lg bg-[#EFF0FF] flex items-center justify-center shrink-0">
                        <Building2 size={14} className="text-[#0410BD]" />
                      </span>
                      <span className="font-medium text-[#12122C] truncate group-hover:text-[#0410BD]">{org.name}</span>
                    </Link>
                  </td>
                  <td className="py-3 px-4 text-[#676687] hidden sm:table-cell font-mono text-xs">{org.slug}</td>
                  <td className="py-3 px-4">
                    <Badge tone={STATUS_TONE[org.status] ?? 'gray'}>{org.status}</Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/workspace/${org.id}`}
                        title="Browse files"
                        className="p-1.5 text-[#BABACE] hover:text-[#0410BD] hover:bg-[#EFF0FF] rounded transition-colors"
                      >
                        <FolderOpen size={14} />
                      </Link>
                      <Link
                        to={`/admin/organizations/${org.id}`}
                        title="Organization settings"
                        className="p-1.5 text-[#BABACE] hover:text-[#676687] hover:bg-[#F2F2F8] rounded transition-colors"
                      >
                        <Settings size={14} />
                      </Link>
                      <Link
                        to={`/workspace/${org.id}`}
                        className="inline-flex items-center gap-1 ml-1 px-2.5 py-1 rounded-[4px] text-xs font-semibold text-[#0410BD] border border-[#0410BD]/30 hover:bg-[#EFF0FF] transition-colors"
                      >
                        Open <ChevronRight size={12} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New organization"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} loading={create.isPending} disabled={!name.trim() || !slug.trim()} className="w-auto">
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="org-name">Name</Label>
            <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <Label htmlFor="org-slug">Slug</Label>
            <Input
              id="org-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+/, ''))}
              placeholder="acme-corp"
            />
            <p className="text-xs text-[#676687] mt-1">Lowercase letters, numbers, and hyphens only.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function OrgCard({ org }: { org: { id: string; name: string; status: string; slug: string } }) {
  return (
    <div className="group relative flex flex-col border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-xl bg-white dark:bg-[#1A1A2E] hover:border-[#0410BD] dark:hover:border-[#818CF8] hover:shadow-sm transition-all overflow-hidden">
      <Link
        to={`/workspace/${org.id}`}
        className="flex items-center gap-3 px-4 py-4 flex-1"
      >
        <span className="w-10 h-10 rounded-lg bg-[#EFF0FF] dark:bg-[#1E2040] flex items-center justify-center shrink-0">
          <Building2 size={20} className="text-[#0410BD] dark:text-[#818CF8]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#12122C] dark:text-white truncate">{org.name}</p>
          <p className="text-xs text-[#BABACE] truncate">{org.slug}</p>
        </div>
        <ChevronRight size={14} className="text-[#BABACE] group-hover:text-[#0410BD] dark:group-hover:text-[#818CF8] transition-colors shrink-0" />
      </Link>
      <div className="flex items-center justify-between px-4 py-2 border-t border-[#F2F2F8] dark:border-[#2A2A4A] bg-[#FAFAFA] dark:bg-[#16162A]">
        <Badge tone={STATUS_TONE[org.status] ?? 'gray'}>{org.status}</Badge>
        <div className="flex items-center gap-1">
          <Link
            to={`/workspace/${org.id}`}
            title="Browse files"
            className="p-1 text-[#BABACE] hover:text-[#0410BD] hover:bg-[#EFF0FF] dark:hover:bg-[#1E2040] rounded transition-colors"
          >
            <FolderOpen size={13} />
          </Link>
          <Link
            to={`/admin/organizations/${org.id}`}
            title="Organization settings"
            className="p-1 text-[#BABACE] hover:text-[#676687] hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] rounded transition-colors"
          >
            <Settings size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}
